"use client";

import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/utils/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { showError } from "@/utils/toast";
import { 
  LayoutDashboard, 
  Folder, 
  FileText, 
  Clock, 
  MessageCircle, 
  User, 
  Download, 
  CheckCircle,
  Bell,
  List
} from "lucide-react";

// Sub-components (imported)
import ClientDashboard from "@/components/ClientDashboard";
import ClientProjects from "@/components/ClientProjects";
import ClientInvoices from "@/components/ClientInvoices";
import ClientTimeReports from "@/components/ClientTimeReports";
import ClientSignedDocs from "@/components/ClientSignedDocs";
import ClientNotifications from "@/components/ClientNotifications";
import ClientProfile from "@/components/ClientProfile";
import ClientOverview from "@/components/ClientOverview";

interface ClientPortalProps {}

const ClientPortal: React.FC<ClientPortalProps> = () => {
  const { profile } = useAuth();
  const { tab = 'overview' } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  // Fetch client-specific data (with error handling)
  const { data: clientData, isLoading } = useQuery({
    queryKey: ['client-portal-data', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      const [projectsRes, invoicesRes, timeRes, notificationsRes] = await Promise.all([
        supabase.from('projects').select('*, tasks(count)').eq('client_id', profile.id),
        supabase.from('invoices').select('*').eq('client_id', profile.id),
        supabase.from('time_logs').select('*, tasks(title)').eq('client_id', profile.id),
        supabase.from('notifications').select('*').eq('user_id', profile.id),
      ]);
      return {
        projects: projectsRes.data || [],
        invoices: invoicesRes.data || [],
        time_logs: timeRes.data || [],
        notifications: notificationsRes.data || [],
      };
    },
    enabled: !!profile?.id && profile.role === 'client',
    retry: (failureCount, error: any) => {
      if (error.message.includes('permission')) return false;
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (!profile) {
      navigate('/login');
      return;
    }
  }, [profile, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Alert>
          <AlertDescription>
            No client data available. Contact your admin.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Derive helpful counts for tabs
  const projectsCount = clientData.projects?.length ?? 0;
  const invoicesCount = clientData.invoices?.length ?? 0;
  const timeCount = clientData.time_logs?.length ?? 0;
  const unreadCount = (clientData.notifications || []).filter((n: any) => !n.read).length;

  return (
    <div className="min-h-screen bg-pattern-minimal">
      <div className="container max-w-6xl mx-auto px-4 py-8">
        {/* Hero header */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border mb-8">
          <div className="p-6 md:p-8 flex items-center gap-4 md:gap-6">
            <div className="h-12 w-12 md:h-14 md:w-14 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold">Client Portal</h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Welcome{profile?.first_name ? `, ${profile.first_name}` : ''}! Access your projects, invoices, time reports, and more.
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Badge variant="secondary">Client</Badge>
              {unreadCount > 0 && <Badge variant="destructive">{unreadCount} unread</Badge>}
            </div>
          </div>
        </div>

        {/* Tabs with icons and counts */}
        <Tabs value={tab} onValueChange={(value: string) => navigate(`/client-portal/${value}`)} className="w-full animate-fade-in-up">
          <TabsList className="grid w-full grid-cols-8 gap-2">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              <span>Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <Folder className="h-4 w-4" />
              <span>Projects</span>
              {projectsCount > 0 && <Badge variant="outline" className="ml-1">{projectsCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span>Invoices</span>
              {invoicesCount > 0 && <Badge variant="outline" className="ml-1">{invoicesCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="time" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Time Reports</span>
              {timeCount > 0 && <Badge variant="outline" className="ml-1">{timeCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="signed" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span>Signed Docs</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span>Notifications</span>
              {unreadCount > 0 && <Badge variant="destructive" className="ml-1">{unreadCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>Profile</span>
            </TabsTrigger>
          </TabsList>

          {/* Content remains, just wrapped in an animated container */}
          <div className="mt-6 space-y-6">
            <TabsContent value="overview">
              <ClientOverview />
            </TabsContent>

            <TabsContent value="dashboard">
              <ClientDashboard 
                projects={clientData.projects} 
                invoices={clientData.invoices} 
                time_logs={clientData.time_logs}
              />
            </TabsContent>

            <TabsContent value="projects">
              <ClientProjects projects={clientData.projects} />
            </TabsContent>

            <TabsContent value="invoices">
              <ClientInvoices invoices={clientData.invoices} />
            </TabsContent>

            <TabsContent value="time">
              <ClientTimeReports time_logs={clientData.time_logs} />
            </TabsContent>

            <TabsContent value="signed">
              <ClientSignedDocs />
            </TabsContent>

            <TabsContent value="notifications">
              <ClientNotifications notifications={clientData.notifications} />
            </TabsContent>

            <TabsContent value="profile">
              <ClientProfile />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default ClientPortal;